import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { fetchEquipmentGroups } from 'store/thunks/adminEquipmentGroupsThunk';
import { IAdminEquipmentGroup } from 'types';
import { ESliceDataFetchingStatus } from 'types/common';

export interface IAdminEquipmentGroupsState {
  groups: IAdminEquipmentGroup[];
  status: ESliceDataFetchingStatus;
  errorMessage: string | null;
  pagesQuantity: number;
  itemsPerPage: number;
  currentPage: number;
  sortBy: string | null;
  sortDir: string | null;
  search: string | null;
  hourlyFrom: string | null;
  hourlyTo: string | null;
  weeklyFrom: string | null;
  weeklyTo: string | null;
  dailyFrom: string | null;
  dailyTo: string | null;
  testFrom: string | null;
  testTo: string | null;
}

const initialState: IAdminEquipmentGroupsState = {
  groups: [],
  status: ESliceDataFetchingStatus.initial,
  pagesQuantity: 1,
  itemsPerPage: 10,
  currentPage: 0,
  errorMessage: null,
  sortBy: null,
  sortDir: null,
  search: null,
  hourlyFrom: null,
  hourlyTo: null,
  weeklyFrom: null,
  weeklyTo: null,
  dailyFrom: null,
  dailyTo: null,
  testFrom: null,
  testTo: null,
};

export const adminEquipmentGroupsSlice = createSlice({
  name: 'adminEquipmentGroups',
  initialState,
  reducers: {
    equipmentGroupsListSetPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerEquipmentGroupsListPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = initialState.currentPage;
    },
    setSort: (
      state,
      action: PayloadAction<{
        sortBy: IAdminEquipmentGroupsState['sortBy'];
        sortDir: IAdminEquipmentGroupsState['sortDir'];
      }>,
    ) => {
      state.sortBy = action.payload.sortBy;
      state.sortDir = action.payload.sortDir;
    },
    setSearch: (state, action: PayloadAction<IAdminEquipmentGroupsState['search']>) => {
      state.search = action.payload;
    },
    setHourlyHashrateFilter: (
      state,
      action: PayloadAction<{
        fromValue: IAdminEquipmentGroupsState['hourlyFrom'];
        toValue: IAdminEquipmentGroupsState['hourlyTo'];
      }>,
    ) => {
      state.hourlyFrom = action.payload.fromValue;
      state.hourlyTo = action.payload.toValue;
    },
    setDailyHashrateFilter: (
      state,
      action: PayloadAction<{
        fromValue: IAdminEquipmentGroupsState['dailyFrom'];
        toValue: IAdminEquipmentGroupsState['dailyTo'];
      }>,
    ) => {
      state.dailyFrom = action.payload.fromValue;
      state.dailyTo = action.payload.toValue;
    },
    setWeeklyHashrateFilter: (
      state,
      action: PayloadAction<{
        fromValue: IAdminEquipmentGroupsState['weeklyFrom'];
        toValue: IAdminEquipmentGroupsState['weeklyTo'];
      }>,
    ) => {
      state.weeklyFrom = action.payload.fromValue;
      state.weeklyTo = action.payload.toValue;
    },
    setTestHashrateFilter: (
      state,
      action: PayloadAction<{
        fromValue: IAdminEquipmentGroupsState['testFrom'];
        toValue: IAdminEquipmentGroupsState['testTo'];
      }>,
    ) => {
      state.testFrom = action.payload.fromValue;
      state.testTo = action.payload.toValue;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchEquipmentGroups.fulfilled, (state, { payload }) => {
        state.status = ESliceDataFetchingStatus.success;
        state.groups = payload.content;
        state.pagesQuantity = payload.details.totalPages;
      })
      .addCase(fetchEquipmentGroups.pending, (state) => {
        state.status = ESliceDataFetchingStatus.loading;
        state.errorMessage = null;
      })
      .addCase(fetchEquipmentGroups.rejected, (state, action) => {
        state.status = ESliceDataFetchingStatus.error;
        state.errorMessage = action.error.message || 'unknown fetch equipment groups list error';
      }),
});

export const selectAdminEquipmentGroups = (state: RootState) => state.adminEquipmentGroups;

export const {
  equipmentGroupsListSetPage,
  setItemsPerEquipmentGroupsListPage,
  setSort,
  setSearch,
  setHourlyHashrateFilter,
  setDailyHashrateFilter,
  setWeeklyHashrateFilter,
  setTestHashrateFilter,
} = adminEquipmentGroupsSlice.actions;

export const adminEquipmentGroupsReducer = adminEquipmentGroupsSlice.reducer;
